import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Img from 'components/base/Img'
import ResponsiveImage from 'components/base/ResponsiveImage'
import Title from 'components/base/Title'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import SectionRule from 'components/base/SectionRule/styles'
import Link, { Theme as LinkTheme } from 'components/base/Link'
import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import AskQuestion from 'components/blocks/AskQuestion'
import RevolutionaryImage from 'assets/images/franchising/revolutionary.svg'
import CarrierImage from 'assets/images/franchising/carrier.svg'
import EffortlessImage from 'assets/images/franchising/effortless.svg'
import NoPressureImage from 'assets/images/franchising/no_pressure.svg'
import SBAImage from 'assets/images/franchising/SBA.svg'

const FranchisingScreen: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Franchising Opportunities | Insurance Lounge'
      description='Explore franchising opportunities with Insurance Lounge’s innovative model. Learn more about financing and building your own insurance franchise.'
      ogImg='/images/og-images/Lounges.webp'
    />
    <PageHero theme={PageHeroTheme.franchising}>
      Insurance Lounge Franchising Opportunities
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <Paragraph theme={ParagraphTheme.introLarge}>
        Our Insurance Lounge Franchise opportunities allow qualified franchisees to open one of our interactive retail storefronts.
        Our hybrid Insurtech format blends the physical presence of a traditional agency with the convenience of shopping online.
        With our independent insurance agency franchise opportunities, you’ll take a step toward the future of entrepreneurship in the insurance business.
      </Paragraph>
      <SectionRule />
      <Paragraph>
        <strong>Learn more about becoming an Insurance Lounge Franchisee.</strong>
      </Paragraph>
      <Paragraph>
        <Link theme={LinkTheme.button} to='/franchising/inquire'>
          Request Info Now
        </Link>
      </Paragraph>
    </Content>
    <Content short={true} margined={true} center={true}>
      <Title level={2}>What Makes Insurance Lounge’s Business Model Better?</Title>
      <Paragraph>
        See a side of insurance that will differentiate you from the competition.
        By pursuing this highly profitable business opportunity with Insurance Lounge, you’ll benefit from leveraging
        hybrid online and in-person services—helping you reach a broader customer base—and working with a
        wide variety of insurance carriers. Our hybrid format blends the professional advice and customer service people expect at a
        brick-and-mortar location with the convenience and no-pressure atmosphere of the online marketplace.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.flex} large={true} center={true}>
      <Content theme={ContentTheme.flexFeatureItem} alignTop={true}>
        <ResponsiveImage height280={true} displayBlock={true}>
          <Img src={RevolutionaryImage} src2x={undefined} alt='Revolutionary Retail' />
        </ResponsiveImage>
        <Title level={3}>Revolutionary Retail Storefronts</Title>
        <Paragraph>
          Meet your customers exactly where they are with retail hours spanning seven days a week with
          an Insurance Lounge <Link theme={LinkTheme.default} to='/locations/storefronts'> storefront location</Link>.
          Close leads from both walk-in traffic and referrals while also seamlessly expanding your customer service
          capabilities via self-quote kiosks, which will help you better prioritize how your employees spend their time.
          Our model is backed by years of research, which will help you develop a successful retail location that’s
          primed for an ideal customer experience.
        </Paragraph>
      </Content>
      <Content theme={ContentTheme.flexFeatureItem} alignTop={true}>
        <ResponsiveImage height280={true} displayBlock={true}>
          <Img src={CarrierImage} src2x={undefined} alt='Carrier Access' />
        </ResponsiveImage>
        <Title level={3}>Carrier Access & Top-Line Commission</Title>
        <Paragraph>
          Joining the Insurance Lounge family will offer you the opportunity to
          work with <Link theme={LinkTheme.default} to='/carriers/'>leading insurance carriers</Link> at top-line contract levels.
          Unlike captive insurance agents, the Insurance Lounge model is an independent agent
          model where you will be able to quote multiple companies side-by-side to offer the
          customer the best coverage rates.
        </Paragraph>
      </Content>
    </Content>
    <Content theme={ContentTheme.flex} large={true} center={true}>
      <Content theme={ContentTheme.flexFeatureItem} alignTop={true}>
        <ResponsiveImage height280={true} displayBlock={true}>
          <Img src={NoPressureImage} src2x={undefined} alt='No Pressure Shopping' />
        </ResponsiveImage>
        <Title level={3}>No Pressure Shopping & Buying Experience</Title>
        <Paragraph>
          At Insurance Lounge, we transformed the Insurance Agent into
          a <Link theme={LinkTheme.default} to='/quote/new'>No-Commission Insurance Concierge<sup>®</sup></Link>.
          This allows the consumer experience to be like no other in the industry.
          Having this unique format affords us consistently higher customer satisfaction,
          close ratios, and expanded opportunities to cross-sell additional products.
        </Paragraph>
      </Content>
      <Content theme={ContentTheme.flexFeatureItem} alignTop={true}>
        <ResponsiveImage height280={true} displayBlock={true}>
          <Img src={EffortlessImage} src2x={undefined} alt='Effortless Lead Generation' />
        </ResponsiveImage>
        <Title level={3}>Effortless Lead Generation</Title>
        <Paragraph>
          When you come on board as an Insurance Lounge Franchisee, not only do you have the advantage of
          walk-in traffic from the look and feel of your Revolutionary Storefront,
          you also get access to leads from our Insurtech E-commerce Platform. Insurance
          Lounge has developed a seamless comparative shopping experience online,
          so consumers can get instant access to multiple carriers’ rates. These
          shoppers are then referred to their local Insurance Lounge Franchisee to
          finalize the sale.
        </Paragraph>
      </Content>
    </Content>
    <Content theme={ContentTheme.flex} large={true} center={true}>
      <Content theme={ContentTheme.flexFeatureItem} alignTop={true}>
        <ResponsiveImage width320={true} displayBlock={true}>
          <Img src={SBAImage} src2x={undefined} alt='SBA-Approved Franchises' />
        </ResponsiveImage>
        <Title level={3}>SBA-Approved Franchises</Title>
        <Paragraph>
          Insurance Lounge has been approved by the Small Business Administration to be included on their franchise directory
          for added financing options for opening your own franchise. Through our funding partner,
          FranFund, franchisees can get approved for long-term, low-interest rate loans that are backed by the SBA,
          as well as other financing options.
        </Paragraph>
        <Paragraph>
          With the recurring revenue model that is achieved with many of the insurance products that we sell,
          Insurance Lounge is a top choice when your goal is to build a thriving and sustainable
          business. <Link theme={LinkTheme.default} to='/franchising/inquire'>Reach out to us today</Link> to take the
          first step toward owning your own franchise and learn more about our franchising opportunities.
        </Paragraph>
      </Content>
    </Content>
    <AskQuestion
      buttons={(
        <Link theme={LinkTheme.button} to='/franchising/inquire'>
          Request Info Now
        </Link>
      )}
      title='All we’re missing is you!'
    >
      Learn more about becoming an Insurance Lounge franchisee. We’d be happy to have you!
    </AskQuestion>
  </div>
)

export default FranchisingScreen
